import {FormikHelpers, useFormik} from "formik";
import {RegistrationApiGet} from "./reg_api";
import {RegEditSchema} from "./RegEditSchema";
import {Box, Button, Card, CardContent, CircularProgress, Typography, useMediaQuery, useTheme} from "@mui/material";
import Title from "../navigation/Title";
import SnackError from "../../components/SnackError";
import RegStatus from "./RegStatus";
import AutocompletePerson from "../people/AutocompletePerson";
import AutocompleteOperation from "../operation/AutocompleteOperation";
import AutocompleteMachine from "../machines/AutocompleteMachine";
import AutocompleteWorkOrder from "../workOrders/AutocompleteWorkOrder";
import AutocompleteProduct from "../products/AutocompleteProduct";
import {DateTimePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import QuantInput from "./QuantInput";
import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveAlt from "@mui/icons-material/SaveAlt";
import React from "react";


export type RegData = Omit<RegistrationApiGet, "id" | "created_at">

interface RegFormProps {
    onSubmit: (values: RegData, helpers: FormikHelpers<any>) => void
    onCancel: () => void
    reg: RegData,
}

export default ({onSubmit, reg, onCancel}: RegFormProps) => {

    const theme = useTheme();
    const md = useMediaQuery(theme.breakpoints.up('md'));

    const {
        values,
        errors,
        isValid,
        submitForm,
        handleChange,
        handleSubmit,
        setFieldValue,
        handleBlur,
        isSubmitting
    } = useFormik<RegData>({
        initialValues: reg,
        onSubmit,
        validationSchema: RegEditSchema
    })

    return <Box component={"form"} onSubmit={handleSubmit}>
        <Title title={"Uredi registracijo"}/>

        <Card>

            <CardContent sx={{display: "flex", flexDirection: "column", gap: "1em"}}>

                <AutocompletePerson
                    value={values.worker}
                    setFieldValue={setFieldValue} label={"Delavec"}
                    name={"worker"}/>
                {errors.worker && <Typography variant={"caption"} sx={{color: "red"}}>{errors.worker}</Typography>}

                <AutocompleteOperation
                    value={values.workTask}
                    setFieldValue={setFieldValue}
                    label={"Operacija"}
                    name={"workTask"}
                />
                {errors.workTask && <Typography variant={"caption"} sx={{color: "red"}}>{errors.workTask}</Typography>}

                <AutocompleteMachine
                    value={values.machine}
                    setFieldValue={setFieldValue}
                    label={"Delovno mesto"}
                    name={"machine"}/>
                {errors.machine && <Typography variant={"caption"} sx={{color: "red"}}>{errors.machine}</Typography>}

                <AutocompleteWorkOrder
                    value={values.workOrder}
                    setFieldValue={setFieldValue}
                    label={"Delovni nalog"}
                    name={"workOrder"}
                />
                {errors.workOrder && <Typography variant={"caption"} sx={{color: "red"}}>{errors.workOrder}</Typography>}

                <AutocompleteProduct
                    value={values.product}
                    setFieldValue={setFieldValue}
                    label={"Produkt"}
                    name={"product"}
                    workOrder={values.workOrder}
                />
                {errors.product && <Typography variant={"caption"} sx={{color: "red"}}>{errors.product}</Typography>}

                <Box sx={{display: "flex", gap: "1em"}}>
                    <DateTimePicker
                        label="Začetek"
                        format={"DD. MM. YYYY HH:mm"}
                        openTo={"hours"}
                        ampm={false}
                        sx={{flexGrow: 1}}
                        value={dayjs(values.StartTime)}
                        onChange={(e) => setFieldValue("StartTime", e?.format())}
                    />
                    {errors.StartTime && <Typography variant={"caption"} sx={{color: "red"}}>{errors.StartTime}</Typography>}

                    <DateTimePicker
                        label="Zaključek"
                        format={"DD. MM. YYYY HH:mm"}
                        openTo={"hours"}
                        sx={{flexGrow: 1}}
                        ampm={false}
                        value={dayjs(values.EndTime)}
                        onChange={(e) => setFieldValue("EndTime", e?.format())}
                    />
                    {errors.EndTime && <Typography variant={"caption"} sx={{color: "red"}}>{errors.EndTime}</Typography>}
                </Box>

                <QuantInput values={values} errors={errors} handleChange={handleChange} handleBlur={handleBlur}/>
            </CardContent>
        </Card>

        <Box sx={{display: "flex", gap: "1em", flexWrap: "wrap", mt: "1em"}}>
            {md && <Button
                variant={"contained"}
                size={"large"}
                title="Nazaj na registracijo"
                onClick={onCancel}>
                <ArrowBack/>
            </Button>}
            <Button
                color={"success"}
                variant={"contained"}
                disabled={isSubmitting || !isValid}
                size={"large"}
                onClick={() => {
                    submitForm();
                }}>{isSubmitting ? <CircularProgress size={"1.5em"}/> : <SaveAlt/>}Shrani</Button>
        </Box>
    </Box>;
}